import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Plus, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function JournalEntryForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { getAuthHeader } = useAuth();
  const { theme } = useTheme();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const isEditing = !!id;
  const [formData, setFormData] = useState({
    entry_date: new Date().toISOString().split('T')[0],
    session_type: 'practice',
    duration_minutes: 60,
    location: '',
    what_practiced: '',
    what_went_well: [],
    needs_improvement: [],
    coach_notes: '',
    mood_rating: 7,
    energy_level: 7,
    confidence_level: 7,
    next_session_goals: [],
    video_urls: []
  });
  const [tempWell, setTempWell] = useState('');
  const [tempImprovement, setTempImprovement] = useState('');
  const [tempGoal, setTempGoal] = useState('');

  useEffect(() => {
    if (isEditing) {
      fetchJournalData();
    }
  }, [id]);

  const fetchJournalData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/journal/entries/${id}`, {
        headers: getAuthHeader()
      });
      setFormData(response.data);
    } catch (error) {
      toast.error('Failed to load journal');
      navigate('/player-dashboard');
    } finally {
      setLoading(false);
    }
  };

  const addToList = (field, value, setValue) => {
    if (value.trim()) {
      setFormData({...formData, [field]: [...formData[field], value.trim()]});
      setValue('');
    }
  };

  const removeFromList = (field, index) => {
    const newList = formData[field].filter((_, i) => i !== index);
    setFormData({...formData, [field]: newList});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.what_practiced.trim()) {
      toast.error('Please describe what you practiced');
      return;
    }

    setSaving(true);
    try {
      if (isEditing) {
        await axios.put(`${API}/journal/entries/${id}`, formData, {
          headers: getAuthHeader()
        });
        toast.success('Journal entry updated!');
      } else {
        await axios.post(`${API}/journal/entries`, formData, {
          headers: getAuthHeader()
        });
        toast.success('Journal saved! +5 points');
      }
      
      // Simple navigation without state
      navigate('/player-dashboard');
    } catch (error) {
      toast.error(isEditing ? 'Failed to update entry' : 'Failed to save entry');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ 
      background: theme === 'dark' 
        ? 'linear-gradient(135deg, #0a0e27 0%, #1a1f3a 100%)' 
        : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
    }}>
      <div className={`backdrop-blur-md ${theme === 'dark' ? 'bg-white/5' : 'bg-black/5'} border-b ${theme === 'dark' ? 'border-white/10' : 'border-black/10'}`}>
        <div className="max-w-4xl mx-auto px-4 py-4">
          <button 
            onClick={() => navigate('/player-dashboard')} 
            className={`flex items-center ${theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} mb-4`}
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </button>
          <h1 className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            {isEditing ? 'Edit Journal Entry' : 'New Journal Entry'}
          </h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <form onSubmit={handleSubmit}>
          <Card className={`glass-card ${theme === 'light' ? 'bg-white/95' : ''}`}>
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={`text-sm font-medium mb-2 block ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Date</label>
                  <Input
                    type="date"
                    value={formData.entry_date}
                    onChange={(e) => setFormData({...formData, entry_date: e.target.value})}
                    className={theme === 'dark' ? 'bg-white/10 border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'}
                  />
                </div>
                <div>
                  <label className={`text-sm font-medium mb-2 block ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Session Type</label>
                  <Select value={formData.session_type} onValueChange={(v) => setFormData({...formData, session_type: v})}>
                    <SelectTrigger className={theme === 'dark' ? 'bg-white/10 border-white/20 text-white' : 'bg-white border-gray-300'}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="practice">Practice</SelectItem>
                      <SelectItem value="match">Match</SelectItem>
                      <SelectItem value="fitness">Fitness</SelectItem>
                      <SelectItem value="mental">Mental Training</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={`text-sm font-medium mb-2 block ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Duration (minutes)</label>
                  <Input
                    type="number"
                    value={formData.duration_minutes}
                    onChange={(e) => setFormData({...formData, duration_minutes: parseInt(e.target.value)})}
                    className={theme === 'dark' ? 'bg-white/10 border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'}
                  />
                </div>
                <div>
                  <label className={`text-sm font-medium mb-2 block ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Location</label>
                  <Input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    placeholder="Cricket ground, nets, etc."
                    className={theme === 'dark' ? 'bg-white/10 border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'}
                  />
                </div>
              </div>

              <div>
                <label className={`text-sm font-medium mb-2 block ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>What I Practiced Today *</label>
                <Textarea
                  value={formData.what_practiced}
                  onChange={(e) => setFormData({...formData, what_practiced: e.target.value})}
                  rows={3}
                  placeholder="Cover drives, pull shots, running between wickets..."
                  className={theme === 'dark' ? 'bg-white/10 border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'}
                />
              </div>

              <div>
                <label className={`text-sm font-medium mb-2 block ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>What Went Well ✅</label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={tempWell}
                    onChange={(e) => setTempWell(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToList('what_went_well', tempWell, setTempWell))}
                    placeholder="Add something that went well..."
                    className={theme === 'dark' ? 'bg-white/10 border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'}
                  />
                  <Button type="button" onClick={() => addToList('what_went_well', tempWell, setTempWell)}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.what_went_well.map((item, idx) => (
                    <span key={`well-${idx}-${item}`} className="px-3 py-1 rounded-full bg-green-500/20 text-green-400 text-sm flex items-center gap-2">
                      {item}
                      <button type="button" onClick={() => removeFromList('what_went_well', idx)}>
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <label className={`text-sm font-medium mb-2 block ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Needs Improvement ⚠️</label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={tempImprovement}
                    onChange={(e) => setTempImprovement(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToList('needs_improvement', tempImprovement, setTempImprovement))}
                    placeholder="Add area to improve..."
                    className={theme === 'dark' ? 'bg-white/10 border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'}
                  />
                  <Button type="button" onClick={() => addToList('needs_improvement', tempImprovement, setTempImprovement)}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.needs_improvement.map((item, idx) => (
                    <span key={`improve-${idx}-${item}`} className="px-3 py-1 rounded-full bg-orange-500/20 text-orange-400 text-sm flex items-center gap-2">
                      {item}
                      <button type="button" onClick={() => removeFromList('needs_improvement', idx)}>
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <label className={`text-sm font-medium mb-2 block ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Coach Notes</label>
                <Textarea
                  value={formData.coach_notes}
                  onChange={(e) => setFormData({...formData, coach_notes: e.target.value})}
                  rows={2}
                  placeholder="Any feedback from your coach..."
                  className={theme === 'dark' ? 'bg-white/10 border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className={`text-sm font-medium mb-2 block ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Mood (1-10)</label>
                  <Input
                    type="range"
                    min="1"
                    max="10"
                    value={formData.mood_rating}
                    onChange={(e) => setFormData({...formData, mood_rating: parseInt(e.target.value)})}
                    className="w-full"
                  />
                  <div className="text-center text-2xl font-bold gradient-text">{formData.mood_rating}</div>
                </div>
                <div>
                  <label className={`text-sm font-medium mb-2 block ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Energy (1-10)</label>
                  <Input
                    type="range"
                    min="1"
                    max="10"
                    value={formData.energy_level}
                    onChange={(e) => setFormData({...formData, energy_level: parseInt(e.target.value)})}
                    className="w-full"
                  />
                  <div className="text-center text-2xl font-bold gradient-text">{formData.energy_level}</div>
                </div>
                <div>
                  <label className={`text-sm font-medium mb-2 block ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Confidence (1-10)</label>
                  <Input
                    type="range"
                    min="1"
                    max="10"
                    value={formData.confidence_level}
                    onChange={(e) => setFormData({...formData, confidence_level: parseInt(e.target.value)})}
                    className="w-full"
                  />
                  <div className="text-center text-2xl font-bold gradient-text">{formData.confidence_level}</div>
                </div>
              </div>

              <div>
                <label className={`text-sm font-medium mb-2 block ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Next Session Goals 🎯</label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={tempGoal}
                    onChange={(e) => setTempGoal(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToList('next_session_goals', tempGoal, setTempGoal))}
                    placeholder="Add goal for next session..."
                    className={theme === 'dark' ? 'bg-white/10 border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'}
                  />
                  <Button type="button" onClick={() => addToList('next_session_goals', tempGoal, setTempGoal)}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.next_session_goals.map((item, idx) => (
                    <span key={`goal-${idx}-${item}`} className="px-3 py-1 rounded-full bg-blue-500/20 text-blue-400 text-sm flex items-center gap-2">
                      {item}
                      <button type="button" onClick={() => removeFromList('next_session_goals', idx)}>
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex gap-4">
                <Button 
                  type="submit" 
                  disabled={saving}
                  className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : (isEditing ? 'Update Entry' : 'Save Entry')}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => navigate('/player-dashboard')}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </Card>
        </form>
      </div>
    </div>
  );
}
